import { Component, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
  retryCount: number;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> | null {
    // Check if it's a chunk load error
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('Failed to load module script')
    ) {
      return { hasChunkError: true };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    if (this.state.hasChunkError && this.state.retryCount < 3) {
      // Log chunk error for monitoring
      console.error('[CHUNK_ERROR] Detected chunk load failure, refreshing...', {
        message: error.message,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        currentUrl: window.location.href,
        retryCount: this.state.retryCount,
      });

      // Auto-reload to get fresh chunks
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Loading updated version...</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
